import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Clock, Users, Building2, Cpu, Layers, Zap, Shield, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "../data/images";
import CTABand from "../components/shared/CTABand";

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const TRACKS = [
  {
    id: "smb",
    icon: Building2,
    color: "#00D4FF",
    label: "Track 01",
    name: "Business & SMB Adoption",
    desc: "For business owners and operators actively using or planning to implement AI. No coding required.",
    sessions: ["AI Tools for Small Business Growth", "Automating Your Operations with AI", "AI ROI: Measuring What Matters", "From Zero to AI: Your First 90 Days"],
  },
  {
    id: "enterprise",
    icon: Layers,
    color: "#7B2FBE",
    label: "Track 02",
    name: "Enterprise & Leadership",
    desc: "For C-suite, VPs, and transformation leads navigating AI at scale across complex organizations.",
    sessions: ["Building an Enterprise AI Strategy", "AI Governance: Balancing Risk and Innovation", "Change Management in the AI Era", "From PoC to Production: The Enterprise Gap"],
  },
  {
    id: "technical",
    icon: Cpu,
    color: "#00D4FF",
    label: "Track 03",
    name: "Technical & Builders",
    desc: "For developers, data scientists, and ML engineers shipping production AI systems.",
    sessions: ["Deploying Agents at Scale", "RAG Architecture Best Practices", "MLOps for Real-World AI", "Building with LLMs: What Actually Works"],
  },
];

const THEMES = [
  { icon: Zap, label: "AI Agents" },
  { icon: Shield, label: "AI Governance" },
  { icon: Globe, label: "Industry Applications" },
  { icon: Users, label: "Workforce & Skills" },
  { icon: Cpu, label: "Infrastructure" },
  { icon: Layers, label: "Data Strategy" },
];

const DAY_SCHEDULE = [
  { time: "8:00 AM", event: "Registration & Breakfast Networking" },
  { time: "9:00 AM", event: "Opening Keynote — Main Stage" },
  { time: "10:15 AM", event: "Expo Hall Opens + Break" },
  { time: "10:45 AM", event: "Parallel Breakout Sessions — All Tracks" },
  { time: "12:30 PM", event: "Networking Lunch" },
  { time: "2:00 PM", event: "Afternoon Sessions + Workshops" },
  { time: "4:00 PM", event: "Roundtable Discussions" },
  { time: "5:30 PM", event: "Expo Happy Hour & Networking Reception" },
];

export default function ProgramPage() {
  return (
    <div data-testid="program-page">
      <section className="page-hero" style={{ backgroundColor: "var(--surface)" }} data-testid="program-hero">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline-label mb-4">Program Overview</div>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-6"
              style={{ fontFamily: "var(--font-heading)" }}>Two days. Three pathways.<br />One intelligent event.</h1>
            <p className="text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
              The Core AI Summit program is built around practical outcomes. Every session is selected for real-world applicability, not resumé-building.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Audience Pathways */}
      <section id="pathways" className="section-py" data-testid="program-pathways-section">
        <div className="container-xl">
          <RevealSection>
            <div className="overline-label mb-4">Audience Pathways</div>
            <h2 className="text-4xl font-black tracking-tight text-white mb-12" style={{ fontFamily: "var(--font-heading)" }}>
              Your track. Your takeaways.
            </h2>
          </RevealSection>

          <div className="space-y-8">
            {TRACKS.map((track, i) => {
              const Icon = track.icon;
              return (
                <RevealSection key={track.id} delay={i * 0.1}>
                  <div className="card p-8" data-testid={`track-card-${track.id}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      <div className="lg:col-span-1">
                        <div className="overline-label mb-3" style={{ color: "var(--text-muted)" }}>{track.label}</div>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                            style={{ background: `${track.color}18`, border: `1px solid ${track.color}33` }}>
                            <Icon size={17} style={{ color: track.color }} />
                          </div>
                          <h3 className="text-xl font-black text-white" style={{ fontFamily: "var(--font-heading)" }}>{track.name}</h3>
                        </div>
                        <p className="text-sm" style={{ color: "var(--text-muted)" }}>{track.desc}</p>
                      </div>
                      <div className="lg:col-span-2">
                        <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                          Sample Sessions
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {track.sessions.map((session) => (
                            <div key={session} className="flex items-start gap-2 p-3 rounded-lg"
                              style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                              <ArrowRight size={12} className="flex-shrink-0 mt-0.5" style={{ color: track.color }} />
                              <span className="text-sm" style={{ color: "var(--text-muted)" }}>{session}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sample Agenda */}
      <section id="agenda" className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="agenda-section">
        <div className="container-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <RevealSection>
              <div>
                <div className="overline-label mb-4">Daily Agenda</div>
                <h2 className="text-4xl font-black tracking-tight text-white mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  What a day looks like.
                </h2>
                <p className="text-base mb-8" style={{ color: "var(--text-muted)" }}>
                  The detailed agenda will be announced as speakers are confirmed. Here's the general daily structure:
                </p>
                <div className="space-y-0">
                  {DAY_SCHEDULE.map((item, i) => (
                    <div key={i} className="flex gap-5 py-4" style={{ borderBottom: i < DAY_SCHEDULE.length - 1 ? "1px solid var(--border)" : "none" }}
                      data-testid={`agenda-item-${i}`}>
                      <div className="w-20 flex-shrink-0">
                        <div className="text-xs font-medium" style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>{item.time}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={13} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                        <span className="text-sm text-white">{item.event}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </RevealSection>

            <RevealSection delay={0.15}>
              <div>
                <div className="overline-label mb-4">Program Themes</div>
                <h3 className="text-3xl font-black tracking-tight text-white mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                  Core topics across all tracks.
                </h3>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {THEMES.map((t) => {
                    const Icon = t.icon;
                    return (
                      <div key={t.label} className="flex items-center gap-3 p-4 rounded-xl"
                        style={{ background: "var(--bg)", border: "1px solid var(--border)" }}
                        data-testid={`theme-chip-${t.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <Icon size={15} style={{ color: "var(--cyan)", flexShrink: 0 }} />
                        <span className="text-sm font-medium" style={{ color: "var(--text-muted)" }}>{t.label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Workshops */}
                <div id="workshops" className="p-6 rounded-2xl" style={{ background: "var(--bg)", border: "1px solid rgba(0,212,255,0.2)" }}>
                  <div className="overline-label mb-3">Workshops & Labs</div>
                  <h4 className="text-xl font-black text-white mb-3" style={{ fontFamily: "var(--font-heading)" }}>Hands-on. Not hypothetical.</h4>
                  <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
                    Half-day and full-day workshops running in parallel with the main program. Limited seats — priority list members get first access.
                  </p>
                  <img src={IMAGES.workshop} alt="Workshop" className="rounded-xl w-full h-40 object-cover opacity-75" />
                </div>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      <CTABand
        title="Ready to Be Part of the Program?"
        subtitle="Join the priority list for early access, or apply to speak at Core AI Summit 2027."
        primaryLabel="Join Priority List"
        secondaryLabel="Apply to Speak"
        secondaryHref="/speakers#apply"
      />
    </div>
  );
}
